/**
 * Agent Chat SSE 스트리밍 훅
 *
 * Supabase Edge Function 'agent-chat'에 SSE 스트리밍 연결을 맺고
 * 실시간 AI 응답, 위젯, 인용, 단계 정보를 상태로 관리한다.
 *
 * 주요 기능:
 * - send(message): 새 메시지 전송 + SSE 스트리밍 시작
 * - stop(): AbortController로 스트림 중단
 * - Exponential backoff 재연결 3회 (500ms → 1500ms → 4500ms)
 * - 위젯 수신 시 React Query 캐시 시딩 (seedQueryCache)
 * - SSE 에러 코드 6종 처리 (SSEErrorCode)
 *
 * 참고: plan/20260410143404-app-frontend-ai-chat-system-design.md §5
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { agentKeys } from "@/api/hooks/query-keys";
import {
  appendTextSegment,
  appendStepSegment,
  appendWidgetSegment,
} from "@modootoday/core-agent/segments";
import type {
  ChatMessage,
  UserMessage,
  AssistantMessage,
  AgentWidget,
  Citation,
  ExecutionStep,
  SSEEvent,
  SSEErrorCode,
  AgentChatRequest,
} from "@modootoday/core-agent/types";

/* ══════════════════════════════════════════════════════════════════════════
   상수
   ══════════════════════════════════════════════════════════════════════════ */

const RECONNECT_DELAYS_MS = [500, 1500, 4500] as const;

/* ══════════════════════════════════════════════════════════════════════════
   타입
   ══════════════════════════════════════════════════════════════════════════ */

export interface UseAgentChatOptions {
  agentId: string;
  initialSessionId?: string;
  /**
   * 초기 메시지 state. ChatZone이 remount될 때 React Query 캐시에서 읽어
   * 전달한다. useState의 lazy initializer에 주입되어 첫 렌더부터 메시지가
   * 노출되고, remount 시 welcome/skeleton으로 깜빡이지 않는다.
   */
  initialMessages?: ChatMessage[];
  context?: Record<string, unknown>;
  /** 스트림 완료 후 콜백 */
  onDone?: (usage: { promptTokens: number; completionTokens: number }) => void;
  /** 에러 발생 시 콜백 */
  onError?: (code: SSEErrorCode, message: string) => void;
}

export interface UseAgentChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  sessionId: string | null;
  steps: ExecutionStep[];
  error: { code: SSEErrorCode; message: string } | null;
  send: (message: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  /**
   * 외부 hydration 훅이 DB 메시지를 주입할 때 사용.
   * use-chat-hydration.ts가 ChatZone에서 호출.
   */
  hydrate: (messages: ChatMessage[], sessionId: string) => void;
}

/* ══════════════════════════════════════════════════════════════════════════
   getEdgeFunctionUrl — agent-chat URL 결정
   ══════════════════════════════════════════════════════════════════════════ */

function getEdgeFunctionUrl(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL is not set");
  return `${supabaseUrl}/functions/v1/agent-chat`;
}

/* ══════════════════════════════════════════════════════════════════════════
   SSE 파싱
   ══════════════════════════════════════════════════════════════════════════ */

function parseSSELine(line: string): SSEEvent | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith("data: ")) return null;
  const json = trimmed.slice(6);
  try {
    return JSON.parse(json) as SSEEvent;
  } catch {
    return null;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   useAgentChat
   ══════════════════════════════════════════════════════════════════════════ */

export function useAgentChat(opts: UseAgentChatOptions): UseAgentChatReturn {
  const {
    agentId,
    initialSessionId,
    initialMessages,
    context,
    onDone,
    onError,
  } = opts;
  const queryClient = useQueryClient();

  // State — 순수 스트리밍 상태만 관리 (DB 로드는 외부 hydration 훅이 담당)
  // initialMessages는 ChatZone의 remount 시 React Query 캐시에서 주입되어
  // 첫 렌더부터 이전 세션 메시지가 보이도록 한다.
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => initialMessages ?? [],
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(
    initialSessionId ?? null,
  );
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [error, setError] = useState<{
    code: SSEErrorCode;
    message: string;
  } | null>(null);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentAssistantIdRef = useRef<string>("");
  const reconnectCountRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  /* ────────────────────────────────────────────────────────────────────
     hydrate — 외부에서 DB 메시지 주입 (use-chat-hydration 훅이 호출)
     스트리밍 중에는 스트림 상태 보호를 위해 무시.
     ──────────────────────────────────────────────────────────────────── */
  const hydrate = useCallback(
    (dbMessages: ChatMessage[], hydrateSessionId: string) => {
      if (isStreaming) {
        // 스트리밍 중 hydration은 무시 (local stream이 우선)
        return;
      }
      setMessages(dbMessages);
      setSessionId(hydrateSessionId);
      setSteps([]);
      setError(null);
    },
    [isStreaming],
  );

  /* ────────────────────────────────────────────────────────────────────
     seedQueryCache — 위젯 데이터를 React Query 캐시에 삽입
     ──────────────────────────────────────────────────────────────────── */
  const seedQueryCache = useCallback(
    (widget: AgentWidget) => {
      queryClient.setQueryData(
        agentKeys.seeded(widget.type, widget.data),
        widget.data,
      );
    },
    [queryClient],
  );

  /* ────────────────────────────────────────────────────────────────────
     updateAssistantMessage — 현재 스트리밍 중인 assistant 메시지 업데이트
     ──────────────────────────────────────────────────────────────────── */
  const updateAssistantMessage = useCallback(
    (updater: (prev: AssistantMessage) => AssistantMessage) => {
      const assistantId = currentAssistantIdRef.current;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId && m.role === "assistant"
            ? updater(m as AssistantMessage)
            : m,
        ),
      );
    },
    [],
  );

  /* ────────────────────────────────────────────────────────────────────
     streamOnce — 단일 SSE 스트림 시도
     ──────────────────────────────────────────────────────────────────── */
  const streamOnce = useCallback(
    async (
      request: AgentChatRequest,
      token: string,
      signal: AbortSignal,
    ): Promise<"done" | "error" | "abort"> => {
      const url = getEdgeFunctionUrl();

      let resp: Response;
      try {
        resp = await fetch(url, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(request),
          signal,
        });
      } catch (err) {
        if (signal.aborted) return "abort";
        throw err;
      }

      if (!resp.ok) {
        let errBody: { code?: string; error?: string } = {};
        try {
          errBody = (await resp.json()) as { code?: string; error?: string };
        } catch {
          /* ignore */
        }
        const code = (errBody.code ?? "INTERNAL") as SSEErrorCode;
        const msg = errBody.error ?? `HTTP ${resp.status}`;
        setError({ code, message: msg });
        onError?.(code, msg);
        return "error";
      }

      if (!resp.body) {
        setError({ code: "INTERNAL", message: "No response body" });
        return "error";
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (true) {
          if (signal.aborted) break;

          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            const event = parseSSELine(line);
            if (!event) continue;

            handleSSEEvent(event, seedQueryCache, updateAssistantMessage, {
              setSessionId,
              setSteps,
              setError,
              setIsStreaming,
              onDone,
              onError,
            });
          }
        }
      } finally {
        reader.releaseLock();
      }

      return signal.aborted ? "abort" : "done";
    },
    [seedQueryCache, updateAssistantMessage, onDone, onError],
  );

  /* ────────────────────────────────────────────────────────────────────
     send
     ──────────────────────────────────────────────────────────────────── */
  const send = useCallback(
    async (message: string) => {
      if (isStreaming) return;

      setError(null);
      setIsStreaming(true);
      reconnectCountRef.current = 0;

      // AbortController 새로 생성
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // User 메시지 추가
      const userMsg: UserMessage = {
        role: "user",
        id: crypto.randomUUID(),
        content: message,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Assistant 메시지 플레이스홀더 추가
      const assistantId = crypto.randomUUID();
      currentAssistantIdRef.current = assistantId;
      const assistantPlaceholder: AssistantMessage = {
        role: "assistant",
        id: assistantId,
        content: "",
        widgets: [],
        citations: [],
        suggestions: [],
        steps: [],
        segments: [], // 이벤트 수신 순서대로 쌓임
        isStreaming: true,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantPlaceholder]);
      setSteps([]);

      // JWT 토큰 획득
      let token: string;
      try {
        const { data: session } = await supabase.auth.getSession();
        token = session.session?.access_token ?? "";
        if (!token) {
          setError({ code: "AUTH_EXPIRED", message: "로그인이 필요합니다." });
          setIsStreaming(false);
          return;
        }
      } catch {
        setError({
          code: "AUTH_EXPIRED",
          message: "인증 토큰을 가져올 수 없습니다.",
        });
        setIsStreaming(false);
        return;
      }

      const request: AgentChatRequest = {
        message,
        sessionId: sessionId ?? undefined,
        agentId,
        context,
      };

      // Exponential backoff 재연결 루프
      let outcome: "done" | "error" | "abort" = "done";
      while (true) {
        try {
          outcome = await streamOnce(request, token, controller.signal);
        } catch {
          if (controller.signal.aborted) {
            outcome = "abort";
            break;
          }
          // 재연결 시도
          const retryIdx = reconnectCountRef.current;
          if (retryIdx >= RECONNECT_DELAYS_MS.length) {
            setError({
              code: "INTERNAL",
              message: "연결에 실패했습니다. 잠시 후 다시 시도해주세요.",
            });
            outcome = "error";
            break;
          }
          const delay = RECONNECT_DELAYS_MS[retryIdx]!;
          reconnectCountRef.current++;
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }

        // 에러 발생 시 재연결 시도
        if (outcome === "error") {
          const retryIdx = reconnectCountRef.current;
          if (retryIdx >= RECONNECT_DELAYS_MS.length) break;
          const delay = RECONNECT_DELAYS_MS[retryIdx]!;
          reconnectCountRef.current++;
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }

        break;
      }

      // 스트리밍 종료 처리
      updateAssistantMessage((prev) => ({ ...prev, isStreaming: false }));
      setIsStreaming(false);

      // 세션 캐시 무효화 — 사이드바 목록, 현재 세션 메타(title 포함), 메시지 목록
      queryClient.invalidateQueries({ queryKey: agentKeys.sessions() });
      if (sessionId) {
        queryClient.invalidateQueries({
          queryKey: agentKeys.session(sessionId),
        });
        queryClient.invalidateQueries({
          queryKey: agentKeys.messages(sessionId),
        });
      }
    },
    [
      isStreaming,
      sessionId,
      agentId,
      context,
      streamOnce,
      updateAssistantMessage,
      queryClient,
    ],
  );

  /* ────────────────────────────────────────────────────────────────────
     stop
     ──────────────────────────────────────────────────────────────────── */
  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    updateAssistantMessage((prev) => ({ ...prev, isStreaming: false }));
    setIsStreaming(false);
  }, [updateAssistantMessage]);

  /* ────────────────────────────────────────────────────────────────────
     reset — 새 대화 시작 (모든 state 초기화)
     ──────────────────────────────────────────────────────────────────── */
  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setIsStreaming(false);
    setSessionId(null);
    setSteps([]);
    setError(null);
    currentAssistantIdRef.current = "";
    // hydration ref는 useChatHydration 훅 내부에서 관리됨
  }, []);

  return {
    messages,
    isStreaming,
    sessionId,
    steps,
    error,
    send,
    stop,
    reset,
    hydrate,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   handleSSEEvent — SSE 이벤트 → 상태 업데이트
   ══════════════════════════════════════════════════════════════════════════ */

interface SSEHandlers {
  setSessionId: (id: string) => void;
  setSteps: React.Dispatch<React.SetStateAction<ExecutionStep[]>>;
  setError: (err: { code: SSEErrorCode; message: string } | null) => void;
  setIsStreaming: (v: boolean) => void;
  onDone?: (usage: { promptTokens: number; completionTokens: number }) => void;
  onError?: (code: SSEErrorCode, message: string) => void;
}

function handleSSEEvent(
  event: SSEEvent,
  seedQueryCache: (widget: AgentWidget) => void,
  updateAssistantMessage: (
    updater: (prev: AssistantMessage) => AssistantMessage,
  ) => void,
  handlers: SSEHandlers,
): void {
  const { setSessionId, setSteps, setError, setIsStreaming, onDone, onError } =
    handlers;

  switch (event.type) {
    case "session":
      setSessionId(event.sessionId);
      break;

    case "step-start":
      setSteps((prev) => {
        const existing = prev.find((s) => s.id === event.step.id);
        if (existing) return prev;
        return [...prev, event.step];
      });
      updateAssistantMessage((prev) => ({
        ...prev,
        steps: [
          ...prev.steps.filter((s) => s.id !== event.step.id),
          event.step,
        ],
        segments: appendStepSegment(prev.segments, event.step),
      }));
      break;

    case "step-done":
      setSteps((prev) =>
        prev.map((s) => (s.id === event.step.id ? event.step : s)),
      );
      updateAssistantMessage((prev) => ({
        ...prev,
        steps: prev.steps.map((s) => (s.id === event.step.id ? event.step : s)),
        segments: appendStepSegment(prev.segments, event.step),
      }));
      break;

    case "text-delta":
      updateAssistantMessage((prev) => ({
        ...prev,
        content: prev.content + event.delta,
        segments: appendTextSegment(prev.segments, event.delta),
      }));
      break;

    case "widget":
      seedQueryCache(event.widget);
      updateAssistantMessage((prev) => ({
        ...prev,
        widgets: [...prev.widgets, event.widget],
        segments: appendWidgetSegment(
          prev.segments,
          event.widget,
          prev.widgets.length,
        ),
      }));
      break;

    case "citation":
      updateAssistantMessage((prev) => ({
        ...prev,
        citations: [...prev.citations, event.citation as Citation],
      }));
      break;

    case "suggestion":
      updateAssistantMessage((prev) => ({
        ...prev,
        suggestions: [...prev.suggestions, ...event.suggestions],
      }));
      break;

    case "done":
      updateAssistantMessage((prev) => ({
        ...prev,
        isStreaming: false,
        usage: event.usage,
      }));
      setIsStreaming(false);
      onDone?.(event.usage);
      break;

    case "error":
      setError(event.error as { code: SSEErrorCode; message: string });
      setIsStreaming(false);
      onError?.(event.error.code as SSEErrorCode, event.error.message);
      break;

    case "confirm-required":
      // Phase 1: 확인 요구는 UI에 노출 (실제 처리는 Phase 2)
      console.warn("[agent-chat] confirm-required:", event.toolCall);
      break;

    default:
      break;
  }
}
